import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Menu, ChevronDown, ChevronRight, Rocket } from 'lucide-react';
import { useQuote } from '../context/QuoteContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { categoryService } from '../services/api';

const Header = () => {
  const { getTotalItems } = useQuote();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryHierarchy, setCategoryHierarchy] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedMobileCategory, setExpandedMobileCategory] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [catData, hierarchyData] = await Promise.all([
          categoryService.getAllCategories(),
          categoryService.getCategoryHierarchy()
        ]);
        setCategories(catData);
        setCategoryHierarchy(hierarchyData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  // Get top categories for main nav (limit to 12)
  const mainCategories = categoryHierarchy.slice(0, 12);

  return (
    <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-pink-600 to-pink-500 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-2">
            <Rocket className="h-4 w-4" />
            <span>Mission Control: 1300 722 659</span>
          </div>
          <div className="hidden md:block">
            <span>info@procloud.com.au</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img 
              src="/procloud-logo.png" 
              alt="ProCloud Promotional" 
              className="h-16 md:h-20 w-auto max-w-[280px]"
            />
          </Link>

          {/* Search Bar - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-2xl">
            <div className="relative w-full">
              <Input
                type="text"
                placeholder="Search promotional products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pr-10"
              />
              <Button
                type="submit"
                size="sm"
                className="absolute right-0 top-0 h-full"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </form>

          {/* Quote Cart & Mobile Menu */}
          <div className="flex items-center gap-2">
            <Link to="/quote-cart">
              <Button variant="outline" className="relative">
                <ShoppingCart className="h-5 w-5" />
                {getTotalItems() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-pink-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {getTotalItems()}
                  </span>
                )}
                <span className="hidden md:inline ml-2">Quote Request</span>
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-2 mt-6">
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-lg font-medium hover:text-pink-600 transition-colors py-2"
                  >
                    Home
                  </Link>
                  <Link
                    to="/products"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-lg font-medium hover:text-pink-600 transition-colors py-2"
                  >
                    All Products
                  </Link>
                  
                  {/* Mobile Category Accordion */}
                  {categoryHierarchy.map(cat => (
                    <div key={cat.category_id} className="border-b">
                      <div 
                        className="flex items-center justify-between py-2 cursor-pointer"
                        onClick={() => setExpandedMobileCategory(
                          expandedMobileCategory === cat.category_id ? null : cat.category_id
                        )}
                      >
                        <Link
                          to={`/products/${cat.category_id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMobileMenuOpen(false);
                          }}
                          className="text-base hover:text-pink-600 transition-colors"
                        >
                          {cat.name}
                        </Link>
                        {cat.subcategories?.length > 0 && (
                          <ChevronDown 
                            className={`h-4 w-4 transition-transform ${
                              expandedMobileCategory === cat.category_id ? 'rotate-180' : ''
                            }`}
                          />
                        )}
                      </div>
                      
                      {/* Mobile Subcategories */}
                      {expandedMobileCategory === cat.category_id && cat.subcategories?.length > 0 && (
                        <div className="pl-4 pb-2 space-y-1">
                          {cat.subcategories.map(sub => (
                            <Link
                              key={sub.subcategory_id}
                              to={`/products/${cat.category_id}?sub=${sub.subcategory_id}`}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-sm text-gray-600 hover:text-pink-600 py-1"
                            >
                              {sub.name} ({sub.product_count})
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <Link
                    to="/contact"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-lg font-medium hover:text-pink-600 transition-colors py-2 mt-2"
                  >
                    Contact Us
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Search Bar - Mobile */}
        <form onSubmit={handleSearch} className="md:hidden mt-4">
          <div className="relative w-full">
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            <Button
              type="submit"
              size="sm"
              className="absolute right-0 top-0 h-full"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </div>

      {/* Navigation - Desktop with Mega Menu */}
      <nav className="hidden md:block border-t bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-1 py-2">
            <Link
              to="/products"
              className="px-3 py-2 text-sm font-medium hover:text-pink-600 hover:bg-pink-50 rounded transition-colors whitespace-nowrap"
            >
              All Products
            </Link>
            
            {mainCategories.map(cat => (
              <div 
                key={cat.category_id}
                className="relative group"
                onMouseEnter={() => setActiveCategory(cat.category_id)}
                onMouseLeave={() => setActiveCategory(null)}
              >
                <Link
                  to={`/products/${cat.category_id}`}
                  className="flex items-center gap-1 px-3 py-2 text-sm hover:text-pink-600 hover:bg-pink-50 rounded transition-colors whitespace-nowrap"
                >
                  {cat.name}
                  {cat.subcategories?.length > 0 && (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </Link>
                
                {/* Mega Menu Dropdown */}
                {cat.subcategories?.length > 0 && activeCategory === cat.category_id && (
                  <div 
                    className="absolute left-0 top-full bg-white shadow-lg rounded-b-lg border border-t-0 min-w-[250px] z-50"
                    onMouseEnter={() => setActiveCategory(cat.category_id)}
                    onMouseLeave={() => setActiveCategory(null)}
                  >
                    <div className="p-4">
                      <Link
                        to={`/products/${cat.category_id}`}
                        className="block text-sm font-semibold text-pink-600 mb-3 hover:underline"
                      >
                        View All {cat.name} →
                      </Link>
                      <div className="grid gap-1">
                        {cat.subcategories.slice(0, 12).map(sub => (
                          <Link
                            key={sub.subcategory_id}
                            to={`/products/${cat.category_id}?sub=${sub.subcategory_id}`}
                            className="flex items-center justify-between text-sm text-gray-700 hover:text-pink-600 hover:bg-pink-50 px-2 py-1.5 rounded transition-colors"
                          >
                            <span>{sub.name}</span>
                            <span className="text-xs text-gray-400">({sub.product_count})</span>
                          </Link>
                        ))}
                        {cat.subcategories.length > 12 && (
                          <Link
                            to={`/products/${cat.category_id}`}
                            className="text-sm text-pink-600 hover:underline px-2 py-1"
                          >
                            +{cat.subcategories.length - 12} more...
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {categoryHierarchy.length > 12 && (
              <div className="relative group">
                <button className="flex items-center gap-1 px-3 py-2 text-sm hover:text-pink-600 hover:bg-pink-50 rounded transition-colors whitespace-nowrap">
                  More
                  <ChevronDown className="h-3 w-3" />
                </button>
                
                {/* More Categories Dropdown */}
                <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-b-lg border border-t-0 min-w-[200px] z-50">
                  <div className="p-2">
                    {categoryHierarchy.slice(12).map(cat => (
                      <Link
                        key={cat.category_id}
                        to={`/products/${cat.category_id}`}
                        className="block text-sm text-gray-700 hover:text-pink-600 hover:bg-pink-50 px-3 py-2 rounded transition-colors"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
